/**
 * BatchFilterToolbar
 *
 * Inline horizontal filter bar for batch results.
 * Controls: verdict dropdown, min composite score input, market dropdown,
 * and 3 quick-preset buttons (GO Only, Top 10, High IRR).
 * Result count shown at the right edge.
 *
 * Filter state is owned by the parent (BatchResultsPage) and synced to URL params.
 */

// ---------------------------------------------------------------------------
// Types (exported for use in BatchResultsPage)
// ---------------------------------------------------------------------------

export interface BatchFilters {
  verdict: 'all' | 'GO' | 'NO-GO' | 'CONDITIONAL';
  minComposite: number | null;
  topN: number | null;
  market: string | null;
  preset: 'none' | 'go-only' | 'top-10' | 'high-irr';
}

export const DEFAULT_BATCH_FILTERS: BatchFilters = {
  verdict: 'all',
  minComposite: null,
  topN: null,
  market: null,
  preset: 'none',
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface BatchFilterToolbarProps {
  filters: BatchFilters;
  onFilterChange: (updates: Partial<BatchFilters>) => void;
  resultCount: number;
  totalCount: number;
  /** Unique market_id values in the full result set for the Market dropdown */
  availableMarkets: string[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BatchFilterToolbar({
  filters,
  onFilterChange,
  resultCount,
  totalCount,
  availableMarkets,
}: BatchFilterToolbarProps) {

  // ---------------------------------------------------------------------------
  // Preset toggle handlers
  // ---------------------------------------------------------------------------

  function handleGoOnly() {
    if (filters.preset === 'go-only') {
      // Deactivate
      onFilterChange({ verdict: 'all', preset: 'none' });
    } else {
      // Activate GO Only preset — clears other presets
      onFilterChange({ verdict: 'GO', topN: null, preset: 'go-only' });
    }
  }

  function handleTop10() {
    if (filters.preset === 'top-10') {
      onFilterChange({ topN: null, preset: 'none' });
    } else {
      onFilterChange({ topN: 10, verdict: 'all', preset: 'top-10' });
    }
  }

  function handleHighIRR() {
    if (filters.preset === 'high-irr') {
      onFilterChange({ preset: 'none' });
    } else {
      onFilterChange({ preset: 'high-irr', verdict: 'all', topN: null });
    }
  }

  function handleClearAll() {
    onFilterChange(DEFAULT_BATCH_FILTERS);
  }

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const hasActiveFilters =
    filters.verdict !== 'all' ||
    filters.minComposite !== null ||
    filters.topN !== null ||
    filters.market !== null ||
    filters.preset !== 'none';

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg border border-border bg-bg-card text-xs">

      {/* Verdict dropdown */}
      <select
        value={filters.verdict}
        onChange={(e) =>
          onFilterChange({
            verdict: e.target.value as BatchFilters['verdict'],
            preset: 'none',
          })
        }
        className="px-2 py-1 rounded border border-border bg-bg-secondary text-text-primary text-xs focus:outline-none focus:ring-1 focus:ring-accent cursor-pointer"
      >
        <option value="all">All Verdicts</option>
        <option value="GO">GO</option>
        <option value="NO-GO">NO-GO</option>
        <option value="CONDITIONAL">CONDITIONAL</option>
      </select>

      {/* Min composite score */}
      <input
        type="number"
        min={0}
        max={100}
        step={1}
        value={filters.minComposite ?? ''}
        placeholder="Min score"
        onChange={(e) => {
          const raw = e.target.value;
          const parsed = raw === '' ? null : parseInt(raw, 10);
          onFilterChange({ minComposite: isNaN(parsed as number) ? null : parsed });
        }}
        className="w-20 px-2 py-1 rounded border border-border bg-bg-secondary text-text-primary text-xs placeholder-text-secondary/50 focus:outline-none focus:ring-1 focus:ring-accent"
      />

      {/* Market dropdown (only rendered when there are multiple markets) */}
      {availableMarkets.length > 1 && (
        <select
          value={filters.market ?? ''}
          onChange={(e) =>
            onFilterChange({ market: e.target.value === '' ? null : e.target.value })
          }
          className="px-2 py-1 rounded border border-border bg-bg-secondary text-text-primary text-xs focus:outline-none focus:ring-1 focus:ring-accent cursor-pointer"
        >
          <option value="">All Markets</option>
          {availableMarkets.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      )}

      {/* Divider */}
      <span className="text-border">|</span>

      {/* Preset buttons */}
      <div className="flex items-center gap-1">
        <PresetButton
          label="GO Only"
          active={filters.preset === 'go-only'}
          onClick={handleGoOnly}
        />
        <PresetButton
          label="Top 10"
          active={filters.preset === 'top-10'}
          onClick={handleTop10}
        />
        <PresetButton
          label="High IRR"
          active={filters.preset === 'high-irr'}
          onClick={handleHighIRR}
        />
      </div>

      {/* Clear all */}
      {hasActiveFilters && (
        <button
          onClick={handleClearAll}
          className="px-2 py-1 text-text-secondary hover:text-text-primary transition-colors underline underline-offset-2 text-[11px]"
        >
          Clear
        </button>
      )}

      {/* Spacer */}
      <span className="flex-1" />

      {/* Result count */}
      <span className="text-text-secondary text-[11px] whitespace-nowrap">
        Showing{' '}
        <span className="text-text-primary font-semibold">{resultCount}</span>
        {' '}of{' '}
        <span className="text-text-primary font-semibold">{totalCount}</span>
        {' '}results
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PresetButton helper
// ---------------------------------------------------------------------------

function PresetButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`
        px-2.5 py-1 rounded text-[11px] font-semibold border transition-colors
        ${
          active
            ? 'bg-accent text-bg-primary border-accent'
            : 'bg-bg-secondary text-text-secondary border-border hover:border-accent/50 hover:text-text-primary'
        }
      `}
    >
      {label}
    </button>
  );
}

export default BatchFilterToolbar;
